import { STATES_OF_RESIDENCE } from '@/constants';
import type { StoreSelectorDocumentDataTabFiltersItem } from '@/types';

export const PICKUP_FILTER_TAB_ALL_ID = '__all__';

export interface PickupFilterTabModel {
  id: string;
  label: string;
  stateCodes: string[];
}

/** Minimal store shape for tab grouping (Treez + external). */
export type PickupTabStoreLike = {
  short_name: string;
  state?: string;
};

const US_STATE_CODE_SET = new Set(STATES_OF_RESIDENCE.map(s => s.value));

/** Parses CSV state codes; drops invalid / empty tokens (fail-safe). */
export function parsePickupTabStateCodes(
  raw: string | null | undefined,
): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(',')
    .map(s => s.trim().toUpperCase())
    .filter(code => code.length === 2 && US_STATE_CODE_SET.has(code));
}

function tabModelsFromPrismic(
  items: StoreSelectorDocumentDataTabFiltersItem[] | null | undefined,
): PickupFilterTabModel[] {
  if (!items?.length) return [];
  return items
    .map((item, index) => {
      const label = item?.label?.trim() ?? '';
      const stateCodes = parsePickupTabStateCodes(item?.state_codes);
      if (!label || stateCodes.length === 0) return null;
      return {
        id: `prismic-tab-${index}`,
        label,
        stateCodes,
      };
    })
    .filter((t): t is PickupFilterTabModel => t != null);
}

function tabModelsFromStoreStates(
  stores: PickupTabStoreLike[],
): PickupFilterTabModel[] {
  const codes = [
    ...new Set(
      stores
        .map(s => s.state?.trim().toUpperCase())
        .filter((c): c is string => Boolean(c && US_STATE_CODE_SET.has(c))),
    ),
  ].sort();
  return codes.map(code => ({
    id: `state-${code}`,
    label: STATES_OF_RESIDENCE.find(s => s.value === code)?.label ?? code,
    stateCodes: [code],
  }));
}

/**
 * Prismic order wins when any valid tab row exists; otherwise one tab per distinct `store.state`.
 */
export function buildPickupFilterTabs(
  prismicRows: StoreSelectorDocumentDataTabFiltersItem[] | null | undefined,
  stores: PickupTabStoreLike[],
  options?: {
    strictPrismic?: boolean;
  },
): PickupFilterTabModel[] {
  const fromCms = tabModelsFromPrismic(prismicRows);
  if (fromCms.length > 0) return fromCms;
  if (options?.strictPrismic) return [];
  return tabModelsFromStoreStates(stores);
}

export function filterStoresByPickupTab(
  tabId: string,
  filterTabs: PickupFilterTabModel[],
  stores: PickupTabStoreLike[],
): PickupTabStoreLike[] {
  if (tabId === PICKUP_FILTER_TAB_ALL_ID) return stores;
  const tab = filterTabs.find(t => t.id === tabId);
  if (!tab) return stores;
  return stores.filter(s => {
    const code = s.state?.trim().toUpperCase();
    return code && tab.stateCodes.includes(code);
  });
}

export function resolvePickupTabIdForSelectedStore(
  selectedStoreId: string,
  stores: PickupTabStoreLike[],
  filterTabs: PickupFilterTabModel[],
): string {
  if (!selectedStoreId) return PICKUP_FILTER_TAB_ALL_ID;
  const store = stores.find(s => s.short_name === selectedStoreId);
  const code = store?.state?.trim().toUpperCase();
  if (!code) return PICKUP_FILTER_TAB_ALL_ID;
  const tab = filterTabs.find(t => t.stateCodes.includes(code));
  return tab?.id ?? PICKUP_FILTER_TAB_ALL_ID;
}
